import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { BlogPostSummary } from "../data/content";
import { fallbackPostsForLocale } from "../data/content";
import { useI18n, type Locale } from "../i18n";
import { ApiRequestError, getPosts } from "../lib/api";
import { formatPublicationDate } from "../lib/date";
import { TopographicLines } from "./TopographicLines";

export function usePublishedPosts() {
  const { locale } = useI18n();
  const [state, setState] = useState<{
    locale: Locale;
    posts: BlogPostSummary[];
    pending: boolean;
    failed: boolean;
  }>(() => ({
    locale,
    posts: [],
    pending: true,
    failed: false
  }));

  useEffect(() => {
    const controller = new AbortController();
    setState({
      locale,
      posts: [],
      pending: true,
      failed: false
    });

    void getPosts(locale, controller.signal)
      .then((nextPosts) => {
        setState((current) =>
          current.locale === locale
            ? {
                locale,
                posts: nextPosts,
                pending: false,
                failed: false
              }
            : current
        );
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        const fallback = fallbackPostsForLocale(locale);
        const canUseFallback =
          fallback.length > 0 &&
          (!(error instanceof ApiRequestError) || error.status >= 500);
        setState((current) =>
          current.locale === locale
            ? {
                locale,
                posts: canUseFallback ? fallback : [],
                pending: false,
                failed: !canUseFallback
              }
            : current
        );
      });

    return () => controller.abort();
  }, [locale]);

  return state.locale === locale
    ? state
    : { locale, posts: [], pending: true, failed: false };
}

export function JournalSection() {
  const { locale, messages } = useI18n();
  const { posts, pending, failed } = usePublishedPosts();
  const visiblePosts = posts.slice(0, 3);
  const copy = messages.blog;

  return (
    <section id="journal" className="journal-preview section" aria-labelledby="journal-title">
      <TopographicLines className="journal-preview__contours" />
      <div className="shell">
        <div className="journal-preview__header">
          <div>
            <h2 id="journal-title">{copy.preview.title}</h2>
            <p>{copy.preview.intro}</p>
          </div>
          <Link className="text-link" to="/journal">
            {copy.preview.readAll}
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
        </div>
        {visiblePosts.length === 0 ? (
          <p className="journal-preview__status" role="status">
            {failed
              ? copy.preview.unavailable
              : pending
                ? copy.article.loading
                : copy.preview.empty}
          </p>
        ) : (
          <div className="story-rail" aria-busy={pending}>
            {visiblePosts.map((post, index) => (
              <article
                className={`story-card${index === 0 ? " story-card--lead" : ""}`}
                key={post.slug}
              >
                <Link
                  className="story-card__media"
                  to={`/journal/${post.slug}`}
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <img
                    src={post.heroImage.src}
                    alt=""
                    width="1536"
                    height="1024"
                    loading="lazy"
                  />
                </Link>
                <div className="story-card__body">
                  <p className="story-card__meta">
                    {copy.categories[post.category] ?? post.category} ·{" "}
                    {post.readingMinutes} {copy.common.minuteShort} ·{" "}
                    {formatPublicationDate(post.publishedAt, "short", locale)}
                  </p>
                  <h3>
                    <Link to={`/journal/${post.slug}`}>{post.title}</Link>
                  </h3>
                  <p>{post.excerpt}</p>
                  <Link className="story-card__arrow" to={`/journal/${post.slug}`}>
                    {copy.common.readNote}
                    <ArrowRight size={18} aria-hidden="true" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
